import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Modal,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import dayjs, { Dayjs } from 'dayjs';
import DateField from 'packages/novous_web/src/common/components/DateField';

interface EditDataModalProps {
  open: boolean;
  onClose: () => void;
  onApply?: (data: OverspeedingData) => void;
  onReset?: () => void;
  cardTitle?: string;
  chartId?: number;
  resetTrigger?: number;
}

interface OverspeedingData {
  dateRange: 'last7days' | 'last15days' | 'last30days' | 'custom';
  startDate: string;
  endDate: string;
  chartId?: number;
}

const EditDataModal: React.FC<EditDataModalProps> = ({
  open,
  onClose,
  onApply,
  onReset,
  cardTitle = 'Overspeeding Events',
  chartId,
  resetTrigger,
}) => {
  const [dateRange, setDateRange] = useState<string>('last7days');
  const [startDate, setStartDate] = useState<string>(dayjs().subtract(7, 'day').format('DD/MM/YYYY'));
  const [endDate, setEndDate] = useState<string>(dayjs().format('DD/MM/YYYY'));

  // Reset state when resetTrigger changes (from parent)
  useEffect(() => {
    setDateRange('last7days');
    setStartDate(dayjs().subtract(7, 'day').format('DD/MM/YYYY'));
    setEndDate(dayjs().format('DD/MM/YYYY'));
  }, [resetTrigger]);

  const handleReset = () => {
    setDateRange('last7days');
    setStartDate(dayjs().subtract(7, 'day').format('DD/MM/YYYY'));
    setEndDate(dayjs().format('DD/MM/YYYY'));
    if (onReset) {
      onReset();
    }
  };

  const convertToYYYYMMDD = (dateString: string): string => {
    if (!dateString) return '';
    const parts = dateString.split('/');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateString;
  };

  const handleApply = () => {
    let calculatedStartDate: string;
    let calculatedEndDate: string = dayjs().format('YYYY-MM-DD');

    // Calculate dates based on selected range
    if (dateRange === 'last7days') {
      calculatedStartDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
    } else if (dateRange === 'last15days') {
      calculatedStartDate = dayjs().subtract(15, 'day').format('YYYY-MM-DD');
    } else if (dateRange === 'last30days') {
      calculatedStartDate = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
    } else if (dateRange === 'custom') {
      calculatedStartDate = startDate
        ? convertToYYYYMMDD(startDate)
        : dayjs().subtract(7, 'day').format('YYYY-MM-DD');
      calculatedEndDate = endDate ? convertToYYYYMMDD(endDate) : dayjs().format('YYYY-MM-DD');
    } else {
      calculatedStartDate = dayjs().subtract(7, 'day').format('YYYY-MM-DD');
    }

    const data: OverspeedingData = {
      dateRange: dateRange as OverspeedingData['dateRange'],
      startDate: calculatedStartDate,
      endDate: calculatedEndDate,
      chartId,
    };

    if (onApply) {
      onApply(data);
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Check if Apply button should be disabled
  const isApplyDisabled = dateRange === 'custom' && (!startDate || !endDate);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="edit-overspeeding-modal"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '90%',
          maxWidth: 500,
          bgcolor: 'white',
          borderRadius: '16px',
          boxShadow: 24,
          p: 4,
          outline: 'none',
        }}
      >
        {/* Modal Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '24px',
              lineHeight: '36px',
              letterSpacing: '0%',
              color: '#1B1B1B',
            }}
          >
            Edit {cardTitle}
          </Typography>
        </Box>

        {/* Subtitle */}
        <Typography
          sx={{
            fontWeight: 400,
            fontStyle: 'normal',
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '0%',
            color: '#767676',
            mb: 2,
          }}
        >
          View {cardTitle.toLowerCase()} for a chosen date range
        </Typography>

        {/* Date Range Radio Buttons */}
        <Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontStyle: 'normal',
              fontSize: '14px',
              lineHeight: '21px',
              letterSpacing: '0%',
              color: '#1B1B1B',
            }}
          >
            Date range
          </Typography>
          <RadioGroup value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2,}}>
              <FormControlLabel
                value="last7days"
                control={
                  <Radio
                    sx={{
                      color: '#5547DD',
                      '&.Mui-checked': {
                        color: '#5547DD',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontStyle: 'normal',
                      fontSize: '16px',
                      lineHeight: '24px',
                      letterSpacing: '0%',
                      color: '#5547DD',
                    }}
                  >
                    Last 7 days
                  </Typography>
                }
              />
              <FormControlLabel
                value="last15days"
                control={
                  <Radio
                    sx={{
                      color: '#5547DD',
                      '&.Mui-checked': {
                        color: '#5547DD',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontStyle: 'normal',
                      fontSize: '16px',
                      lineHeight: '24px',
                      letterSpacing: '0%',
                      color: '#5547DD',
                    }}
                  >
                    Last 15 days
                  </Typography>
                }
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControlLabel
                value="last30days"
                control={
                  <Radio
                    sx={{
                      color: '#5547DD',
                      '&.Mui-checked': {
                        color: '#5547DD',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontStyle: 'normal',
                      fontSize: '16px',
                      lineHeight: '24px',
                      letterSpacing: '0%',
                      color: '#5547DD',
                    }}
                  >
                    Last 30 days
                  </Typography>
                }
              />
              <FormControlLabel
                value="custom"
                control={
                  <Radio
                    sx={{
                      color: '#5547DD',
                      '&.Mui-checked': {
                        color: '#5547DD',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontStyle: 'normal',
                      fontSize: '16px',
                      lineHeight: '24px',
                      letterSpacing: '0%',
                      color: '#5547DD',
                    }}
                  >
                    Custom range
                  </Typography>
                }
              />
            </Box>
          </RadioGroup>
        </Box>

        {/* Date Pickers */}
        <Box sx={{ mb: 2 }}>
          <DateField
            label="Start Date"
            name="startDate"
            value={startDate}
            onChange={(value) => setStartDate(value || '')}
            isDisabled={dateRange !== 'custom'}
            maxDate={endDate || dayjs().format('DD/MM/YYYY')}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <DateField
            label="End Date"
            name="endDate"
            value={endDate}
            onChange={(value) => setEndDate(value || '')}
            isDisabled={dateRange !== 'custom'}
            minDate={startDate}
            maxDate={dayjs().format('DD/MM/YYYY')}
          />
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
            sx={{
              flex: 1,
              borderRadius: '8px',
              borderColor: '#5547DD',
              color: '#5547DD',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              padding: '12px',
              '&:hover': {
                borderColor: '#5547DD',
                backgroundColor: '#F5F4FD',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disabled={isApplyDisabled}
            sx={{
              flex: 1,
              borderRadius: '8px',
              backgroundColor: isApplyDisabled ? '#CCCCCC' : '#5547DD',
              color: '#FFFFFF',
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              padding: '12px',
              cursor: isApplyDisabled ? 'not-allowed' : 'pointer',
              '&:hover': {
                backgroundColor: isApplyDisabled ? '#CCCCCC' : '#4338CA',
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditDataModal;
